export let mixin = ( { node = null, config = {} } = {} ) => {

    if ( ! node ) {
        return false;
    }

    config = Object.assign(
        {
            focusableSelector: `[data-js="focusable"]`,
            tabindexDataAttr: 'tabindex',
            breakpoint: 1024
        },
        config
    );

    let state = {
        node,
        config,
        focusables: node.querySelectorAll( config.focusableSelector ),
        isTabbing: false
    };

    let module = {

        /* Event handlers -------------------- */

        handleFocus( e ) {

            if ( ! state.isTabbing || window.innerWidth < state.config.breakpoint ) {
                return;
            }

            const focusable = e.target;
            const tabindex = this.getFocusableTabindex( focusable );
            const naturalTabindex = this.getFocusableNaturalTabindex( focusable );

            const isFromOutside = this.getFocusableNaturalTabindex( e.relatedTarget ) === -1;

            // If we're tabbing into the login card's natural tabindex from elsewhere, we need to focus the correct elements
            if ( isFromOutside ) {

                // If tabbing forwards towards the first natural focusable
                if ( naturalTabindex === 0 ) {
                    this.getFocusableByTabindex( 0 ).focus();
                }

                // If tabbing backwards towards the last natural focusable
                else if ( naturalTabindex === state.focusables.length - 1 ) {
                    this.getFocusableByTabindex( state.focusables.length - 1 ).focus();
                }
            }                        
        },

        handleBlur( e ) {

            const isToOutside = this.getFocusableNaturalTabindex( e.relatedTarget ) === -1;

            // Pretty hacky way to restore tab access to the login card once we've left it via a different pretty hacky way described below
            if ( isToOutside ) {

                [...state.focusables].map(
                    ( focusable ) => focusable.removeAttribute( 'tabindex' )
                );
            }
        },

        handleKeydown( e ) {

            if ( window.innerWidth < state.config.breakpoint ) {
                return;
            }

            if ( e.key === 'Tab' ) {

                const tabbingBackwards = e.shiftKey;

                const focusable = e.currentTarget;
                const tabindex = this.getFocusableTabindex( focusable );
                const nextTabindex = tabbingBackwards ? tabindex - 1 : tabindex + 1;
                
                // If we're tabbing out of the bounds of the login card, we need to handle that accordingly
                // Pretty hacky way to ensure we hit the next sensible tabbable element without loading a massive lib like jQueryUI
                // Removes tab access to all focusables within the card so that the only place to go is outside
                if ( nextTabindex < 0 || nextTabindex >= state.focusables.length ) {

                    [...state.focusables].map(
                        ( focusable ) => focusable.setAttribute( 'tabindex', -1 )
                    );
                    return;
                }

                // Otherwise, we just keep on focusin'
                e.preventDefault();
                this.getFocusableByTabindex( nextTabindex ).focus();
            }
        },

        /* Utils -------------------------------------------- */

        getFocusableTabindex( focusable ) {
            return parseInt( focusable.getAttribute( `data-${state.config.tabindexDataAttr}` ) );
        },

        getFocusableByTabindex( tabindex ) {
            return [...state.focusables].find(
                ( focusable ) => this.getFocusableTabindex( focusable ) === tabindex
            );
        },

        getFocusableNaturalTabindex( focusable ) {
            return [...state.focusables].indexOf( focusable );
        },

        /* Setup & initialization --------------------------- */

        setEventBindings() {

            if ( ! state.focusables.length ) {
                return;
            }

            window.addEventListener( 'keydown', ( e ) => state.isTabbing = e.key === 'Tab' || state.isTabbing );
            window.addEventListener( 'keyup', ( e ) => state.isTabbing = e.key !== 'Tab' || state.isTabbing );

            [...state.focusables].map(
                ( focusable ) => {
                    focusable.addEventListener( 'focus', this.handleFocus.bind( this ) );
                    focusable.addEventListener( 'blur', this.handleBlur.bind( this ) );
                    focusable.addEventListener( 'keydown', this.handleKeydown.bind( this ) );
                }
            );
        },

        init() {
            this.setEventBindings();
        },
    };

    return module.init();
};

export default {

    init( { selector, config = {} } ) {

        let selected = document.querySelectorAll( selector );

        if ( ! selected.length ) {
            return;
        }

        return [...selected].map(
            ( node, index ) => mixin( { node, config } )
        );
    },
};
